import { Query } from "@apollo/client/react/components";
import OverdueSubscriptionStickyBanner from "components/billing/OverdueSubscriptionStickyBanner";
import AmericanCompany from "components/company/AmericanCompany";
import AmericanCompanyContext from "components/company/AmericanCompanyContext";
import LocaleContext from "components/i18n/LocaleContext";
import WholeIssuerPageSkeleton from "components/issuers/WholeIssuerPageSkeleton";
import NavbarLayout from "components/layouts/NavbarLayout";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import type { NextPage } from "next";
import NotFound from "pages/404";
import { ISecurityCountry } from "types/graphqlTypes";

interface IAmericanCompanyOrReitProps {
  tickerSymbol: string | null;
}

const AmericanCompanyOrReit: NextPage<IAmericanCompanyOrReitProps> = function AmericanCompanyOrReit({ tickerSymbol }) {
  const normalizedTickerSymbol = (tickerSymbol || "").toUpperCase();
  // Test for potential invalid ticker symbols
  if (!normalizedTickerSymbol || normalizedTickerSymbol.length > 8 || /^([0-9]+)$/.test(normalizedTickerSymbol)) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  return (
    <Query<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>
      query={IssuerByTickerSymbolQuery}
      variables={{
        tickerSymbol: normalizedTickerSymbol,
        country: ISecurityCountry.Us,
      }}
    >
      {({ data, loading }) => {
        if (loading) {
          return (
            <NavbarLayout>
              <WholeIssuerPageSkeleton />
            </NavbarLayout>
          );
        }

        if (!data || !data.issuerByTickerSymbol || data.issuerByTickerSymbol.__typename !== "AmericanCompanyIssuer") {
          return (
            <>
              <SetHttpStatusCode httpStatus={404} />
              <NotFound />
            </>
          );
        }

        const security = getMatchedSecurity(data.issuerByTickerSymbol, normalizedTickerSymbol);
        if (!security) {
          return (
            <>
              <SetHttpStatusCode httpStatus={404} />
              <NotFound />
            </>
          );
        }

        return (
          <NavbarLayout>
            <OverdueSubscriptionStickyBanner />

            <LocaleContext.Provider value="USA">
              <AmericanCompanyContext.Provider value={data.issuerByTickerSymbol}>
                <AmericanCompany
                  issuer={data.issuerByTickerSymbol}
                  tickerSymbol={normalizedTickerSymbol}
                  security={security}
                />
              </AmericanCompanyContext.Provider>
            </LocaleContext.Provider>
          </NavbarLayout>
        );
      }}
    </Query>
  );
};

function getMatchedSecurity(issuer: IAmericanCompanyIssuerTopLevelFragment, forTickerSymbolQuery: string) {
  if (issuer.__typename !== "AmericanCompanyIssuer") {
    return null;
  }
  const matchedSecurity = issuer.securities.find((candidate) => {
    return candidate.tickerSymbol === forTickerSymbolQuery;
  });
  if (matchedSecurity) {
    return matchedSecurity;
  }

  return null;
}

AmericanCompanyOrReit.getInitialProps = async ({ query }) => ({
  tickerSymbol: getQueryStringValue(query, "tickerSymbol"),
});

export default AmericanCompanyOrReit;
